import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventsApi, qaApi } from '@/api/client';
import { useEventChat } from '@/hooks/useEventChat';
import { AuthorName } from '@/components/events/AuthorName';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useRef, useEffect } from 'react';
import { Send, ThumbsUp, MessageSquare, HelpCircle, Users, Clock, LogIn } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/apiError';
import type { QAQuestion } from '@/types';

export const SIGN_IN_TO_VIEW = 'Sign in to view this event';
export const EVENT_NOT_FOUND = 'Event not found';

// Public page (#1177): a public or unlisted event's share link works signed
// out. The slug lookup answers per caller (#1175): the event's org gets the
// whole event, anyone else the public projection of a public/unlisted event,
// and a private event is 404, the same as an unknown slug. So a signed-out
// 404 might be a private event: offer sign-in (login() returns to this
// path). Signed in, a 404 is "Event not found", private or not.
export default function EventViewer() {
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [activeTab, setActiveTab] = useState<'chat' | 'qa'>('chat');
  const [chatInput, setChatInput] = useState('');
  const [qaInput, setQaInput] = useState('');
  // A signed-out viewer's chat name, where the event allows one (JSS#1191):
  // applied on Set, not per keystroke (each change reconnects the chat).
  const [nameInput, setNameInput] = useState('');
  const [chatName, setChatName] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['event-slug', slug, isAuthenticated],
    queryFn: () => eventsApi.getBySlug(slug!),
    // Wait for the session: a signed-in member must send their bearer, or
    // their own private event answers 404.
    enabled: !!slug && !authLoading,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const { data: questions = [] } = useQuery({
    queryKey: ['qa-questions', event?.id],
    queryFn: () => qaApi.list(event!.id),
    enabled: !!event?.id,
    refetchInterval: 5000,
  });

  const anonymousNames = !user && event?.config?.anonymousNames === true;
  const refreshQuestions = () => queryClient.invalidateQueries({ queryKey: ['qa-questions', event?.id] });

  // The server assigns who wrote a message (JSS#1191): no user id or name is
  // sent, only a signed-out viewer's asked-for name where the event allows it.
  const { messages, sendMessage, isConnected, viewerCount } = useEventChat({
    eventId: event?.id ?? '',
    displayName: anonymousNames && chatName ? chatName : undefined,
    // social-service refuses a chat the event turns off to everyone but its
    // members (JSS#1187); don't reconnect into that refusal.
    enabled: !!event?.id && event?.status === 'live' && event?.config?.chatEnabled !== false,
    // Q&A changes arrive on the chat socket, as this viewer may see them.
    onQaChange: () => void refreshQuestions(),
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-jeket-black">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jeket-gold" />
      </div>
    );
  }

  if (!event) {
    const notFound = !error || (error instanceof ApiError && error.status === 404);
    if (notFound && !isAuthenticated) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 min-h-screen bg-jeket-black">
          <p className="text-jeket-silver-bright">{SIGN_IN_TO_VIEW}</p>
          <button
            type="button"
            onClick={() => void login()}
            className="flex items-center gap-2 px-4 py-2 rounded bg-jeket-gold/20 text-jeket-gold hover:bg-jeket-gold/30"
          >
            <LogIn size={16} /> Sign in
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-jeket-black">
        <p className="text-jeket-silver-muted">{notFound ? EVENT_NOT_FOUND : "Couldn't load this event — try again shortly."}</p>
      </div>
    );
  }

  const handleSendChat = () => {
    if (chatInput.trim()) {
      sendMessage(chatInput.trim());
      setChatInput('');
    }
  };

  const handleSubmitQuestion = async () => {
    if (qaInput.trim() && event?.id && user) {
      await qaApi.submit(event.id, { content: qaInput.trim() });
      setQaInput('');
      void refreshQuestions();
    }
  };

  const isPreEvent = ['created', 'scheduled', 'warmup'].includes(event.status);
  const isLive = event.status === 'live';
  const isPostEvent = ['ended', 'post_processing', 'archived'].includes(event.status);

  return (
    <div className="min-h-screen bg-jeket-black text-jeket-silver-bright flex flex-col">
      {/* Header */}
      <header className="bg-jeket-black-surface border-b border-jeket-gold/20 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Jeket" className="h-6" />
          <h1 className="text-lg font-semibold">{event.name}</h1>
          {isLive && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-jeket-healthy/20 text-jeket-healthy">LIVE</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-jeket-silver-muted">
          {isLive && (
            <span className="flex items-center gap-1">
              <Users size={14} />
              {viewerCount}
            </span>
          )}
          {isConnected && <span className="text-jeket-healthy text-xs">Connected</span>}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 flex items-center justify-center p-8">
          {isPreEvent && (
            <div className="text-center space-y-4">
              <Clock size={48} className="mx-auto text-jeket-gold/50" />
              <h2 className="text-2xl font-bold">{event.name}</h2>
              <p className="text-jeket-silver-muted max-w-md">{event.description}</p>
              {event.scheduled_start && (
                <p className="text-jeket-gold">
                  Starts {new Date(event.scheduled_start).toLocaleString()}
                </p>
              )}
            </div>
          )}
          {isLive && (
            <div className="w-full max-w-4xl aspect-video bg-jeket-black-elevated rounded-lg flex items-center justify-center border border-jeket-gold/10">
              <p className="text-jeket-silver-muted">Video player area</p>
            </div>
          )}
          {isPostEvent && (
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-bold">Event has ended</h2>
              <p className="text-jeket-silver-muted">{event.name}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        {(isLive || event.config.qaEnabled) && (
          <aside className="w-96 border-l border-jeket-gold/20 bg-jeket-black-surface flex flex-col">
            {/* Tab bar */}
            <div className="flex border-b border-jeket-gold/20">
              {event.config.chatEnabled && (
                <button
                  onClick={() => setActiveTab('chat')}
                  className={cn(
                    'flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2',
                    activeTab === 'chat' ? 'text-jeket-gold border-b-2 border-jeket-gold' : 'text-jeket-silver-muted'
                  )}
                >
                  <MessageSquare size={14} /> Chat
                </button>
              )}
              {event.config.qaEnabled && (
                <button
                  onClick={() => setActiveTab('qa')}
                  className={cn(
                    'flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2',
                    activeTab === 'qa' ? 'text-jeket-gold border-b-2 border-jeket-gold' : 'text-jeket-silver-muted'
                  )}
                >
                  <HelpCircle size={14} /> Q&A
                </button>
              )}
            </div>

            {/* Chat tab */}
            {activeTab === 'chat' && (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {messages.map((msg) => (
                    <div key={msg.id} className="text-sm">
                      <AuthorName name={msg.username} verified={msg.verified} className="font-medium text-jeket-gold" />
                      {msg.mine && <span className="ml-1 text-xs text-jeket-silver-muted">(you)</span>}{' '}
                      <span className="text-jeket-silver-bright">{msg.content}</span>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                {isLive && anonymousNames && (
                  <div className="px-3 pt-3 flex gap-2 items-center text-xs text-jeket-silver-muted">
                    <input
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && setChatName(nameInput.trim())}
                      placeholder="Your name (shown as unverified)"
                      maxLength={50}
                      aria-label="Your chat name"
                      className="flex-1 bg-jeket-black-elevated border border-jeket-gold/20 rounded px-2 py-1 text-xs text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
                    />
                    <button
                      onClick={() => setChatName(nameInput.trim())}
                      className="px-2 py-1 rounded bg-jeket-gold/20 text-jeket-gold hover:bg-jeket-gold/30"
                    >
                      Set
                    </button>
                  </div>
                )}
                {isLive && (
                  <div className="p-3 border-t border-jeket-gold/20">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                        placeholder="Send a message..."
                        maxLength={500}
                        className="flex-1 bg-jeket-black-elevated border border-jeket-gold/20 rounded px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
                      />
                      <button
                        onClick={handleSendChat}
                        className="p-2 bg-jeket-gold/20 text-jeket-gold rounded hover:bg-jeket-gold/30"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Q&A tab */}
            {activeTab === 'qa' && (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {questions
                    .filter((q: QAQuestion) => q.status !== 'dismissed')
                    .map((q: QAQuestion) => (
                      <div key={q.id} className="bg-jeket-black-elevated rounded-lg p-3 space-y-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <AuthorName name={q.username} verified={q.verified} className="text-sm font-medium text-jeket-gold" />
                            <p className="text-sm text-jeket-silver-bright mt-1">{q.content}</p>
                          </div>
                          <button
                            onClick={() => void qaApi.upvote(q.id).then(refreshQuestions)}
                            className="flex items-center gap-1 text-xs text-jeket-silver-muted hover:text-jeket-gold"
                          >
                            <ThumbsUp size={12} />
                            {q.upvotes}
                          </button>
                        </div>
                        {q.answer && (
                          <div className="mt-2 pl-3 border-l-2 border-jeket-gold/30">
                            <p className="text-sm text-jeket-silver-bright">{q.answer}</p>
                            <p className="text-xs text-jeket-silver-muted mt-1">
                              Answered by {q.answered_by}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                {(isLive || isPreEvent) && !user && (
                  <div className="p-3 border-t border-jeket-gold/20">
                    <button
                      type="button"
                      onClick={() => void login()}
                      className="w-full text-sm text-jeket-gold hover:underline"
                    >
                      Sign in to ask a question
                    </button>
                  </div>
                )}
                {(isLive || isPreEvent) && user && (
                  <div className="p-3 border-t border-jeket-gold/20">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={qaInput}
                        onChange={(e) => setQaInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmitQuestion()}
                        placeholder="Ask a question..."
                        className="flex-1 bg-jeket-black-elevated border border-jeket-gold/20 rounded px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
                      />
                      <button
                        onClick={handleSubmitQuestion}
                        className="p-2 bg-jeket-gold/20 text-jeket-gold rounded hover:bg-jeket-gold/30"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
